// Lecture dans la page. hls.js prend en charge le HLS partout où Media Source
// Extensions existe ; Safari le lit nativement. Les fichiers progressifs sont
// donnés tels quels à <video>.

import Hls from "hls.js";
import { warm } from "./preload.ts";
import type { Preload, WarmProgress } from "./preload.ts";
import type { Stream } from "./stream.ts";

/** Choix de qualité, conservé d'un épisode à l'autre.
 *
 *  `auto` laisse l'ABR de hls.js arbitrer au fil du débit, `best` épingle la
 *  variante la plus haute, un nombre épingle une hauteur (720 pour du 720p).
 *  On retient une hauteur plutôt qu'un index hls.js : d'un lecteur à l'autre,
 *  les épisodes n'offrent ni le même nombre de variantes ni le même ordre. */
export type Quality = "auto" | "best" | number;

/** Une entrée du sélecteur de qualité. */
export interface Choice {
  label: string;
  /** Valeur de l'`<option>`, et ce qui part dans localStorage. */
  value: string;
}

export function serializeQuality(quality: Quality): string {
  return typeof quality === "number" ? String(quality) : quality;
}

export function parseQuality(raw: string | null): Quality {
  if (raw === "best") return "best";

  const height = Number(raw);

  return Number.isInteger(height) && height > 0 ? height : "auto";
}

/** Relevé instantané de la lecture, pour le mode développeur. */
export interface Stats {
  /** Nombre de variantes annoncées par le manifeste. */
  levels: number;
  /** Hauteur du niveau réellement joué, 0 si inconnue. */
  height: number;
  /** Débit déclaré de ce niveau, en bits par seconde. */
  bitrate: number;
  /** Débit mesuré par hls.js, en bits par seconde. */
  bandwidth: number;
  /** L'ABR décide-t-il du niveau ? */
  auto: boolean;
  /** Secondes déjà en tampon devant la tête de lecture. */
  buffer: number;
  /** Segments chargés depuis le début de l'épisode, et erreurs rencontrées. */
  frags: number;
  errors: number;
  /** Durée de chargement du dernier segment, en millisecondes. */
  latency: number;
  /** Images perdues sur images décodées. */
  dropped: number;
  frames: number;
}

/** Reprises tentées sur une erreur fatale avant d'abandonner. Sans plafond, un
 *  CDN qui ne répond plus faisait retenter hls.js indéfiniment : le lecteur
 *  restait figé, sans le moindre message. */
const MAX_RECOVERIES = 3;

/** Écart toléré entre la durée du média et celle de la playlist, en secondes.
 *  Les deux ne coïncident jamais tout à fait — les `#EXTINF` sont arrondis —
 *  mais l'écart se compte en fractions de seconde. */
const DURATION_SLACK = 30;

/** Rechargements sur durée incohérente, par épisode. */
const MAX_RELOADS = 2;

export class Player {
  private hls: Hls | null = null;
  private stream: Stream | null = null;
  private quality: Quality = "auto";
  private preload: Preload = "normal";
  private warming: AbortController | null = null;
  private frags = 0;
  private errors = 0;
  private latency = 0;
  private recoveries = 0;
  private reloads = 0;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly onChoices: (choices: Choice[], current: string) => void,
    private readonly onError: (message: string) => void,
    private readonly onPreload: (progress: WarmProgress | null) => void,
  ) {
    video.addEventListener("durationchange", () => this.checkDuration());
  }

  load(stream: Stream): void {
    this.reloads = 0;
    this.attach(stream, -1, true);
  }

  /** `startPosition` vaut -1 pour laisser hls.js partir du début. */
  private attach(stream: Stream, startPosition: number, autoplay: boolean): void {
    this.destroy();

    this.stream = stream;
    this.frags = 0;
    this.errors = 0;
    this.latency = 0;
    this.recoveries = 0;

    if (!stream.isHLS || !Hls.isSupported()) {
      this.video.src = stream.url;
      this.onChoices([], serializeQuality(this.quality));
      void this.video.play().catch(() => undefined);

      return;
    }

    const hls = new Hls({
      // le proxy relaie déjà tout : inutile de retenter agressivement
      manifestLoadingMaxRetry: 2,
      levelLoadingMaxRetry: 2,
      fragLoadingMaxRetry: 4,
      // On fixe nous-mêmes le niveau de départ, une fois le manifeste connu.
      // Sans ça hls.js part de son estimation par défaut — 500 kb/s, donc la
      // variante la plus basse — et ne remonte qu'après plusieurs segments.
      autoStartLoad: false,
      startPosition,
      // Hors mode normal, on repousse le plafond du tampon : 30 s et 60 Mo par
      // défaut, soit à peine trois minutes d'avance. hls.js redescend de
      // lui-même si le navigateur refuse d'en garder autant.
      ...(this.preload === "normal"
        ? {}
        : { maxBufferLength: 300, maxBufferSize: 400 * 1024 * 1024 }),
    });

    this.hls = hls;

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.applyQuality();
      this.publishChoices();

      // le premier fragment n'est demandé qu'ici, le niveau étant arrêté
      // la position doit être redonnée ici : sans argument, `startLoad` repart
      // du début sans consulter `startPosition`
      hls.startLoad(startPosition);

      if (autoplay) void this.video.play().catch(() => undefined);
    });

    // Le préchargement démarre une fois le premier fragment en tampon : avant
    // ça, il disputerait ses connexions à la lecture elle-même. Il porte sur le
    // niveau réellement joué — épingler une qualité évite qu'une bascule ABR
    // rende inutile ce qui a déjà été tiré.
    hls.on(Hls.Events.FRAG_BUFFERED, (_event, data) => {
      // la lecture avance : une erreur passée ne compte plus contre la suivante
      this.recoveries = 0;

      if (this.preload !== "full" || this.warming) return;

      const details = hls.levels[data.frag.level]?.details;
      if (!details) return;

      // `sn` vaut "initSegment" pour le segment d'initialisation fMP4 : lui
      // n'a pas de rang dans la suite, hls.js l'a déjà chargé de toute façon.
      const from = data.frag.sn;
      if (typeof from !== "number") return;

      const ahead = details.fragments
        .filter((fragment) => typeof fragment.sn === "number" && fragment.sn > from)
        .map((fragment) => fragment.url);

      if (ahead.length === 0) return;

      this.startWarming(ahead);
    });

    // Compteurs du mode développeur. `FRAG_LOADED` porte le temps de
    // chargement mesuré par hls.js, plus juste qu'un chronomètre à nous : il
    // exclut l'attente en file quand plusieurs segments sont demandés.
    hls.on(Hls.Events.FRAG_LOADED, (_event, data) => {
      this.frags++;

      const { loading } = data.frag.stats;

      if (loading.first > 0 && loading.end > 0) {
        this.latency = Math.round(loading.end - loading.start);
      }
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      this.errors++;

      if (!data.fatal) return;

      if (this.recoveries < MAX_RECOVERIES) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            this.recoveries++;
            hls.startLoad();
            return;
          case Hls.ErrorTypes.MEDIA_ERROR:
            this.recoveries++;
            hls.recoverMediaError();
            return;
        }
      }

      this.fail("Lecture interrompue : " + data.details);
    });

    hls.loadSource(stream.url);
    hls.attachMedia(this.video);
  }

  /** Coupe la vidéo avant de prévenir : laisser l'image en place ferait croire
   *  que la lecture continue, ou la relancerait au prochain clic. */
  private fail(message: string): void {
    this.destroy();
    this.onError(message);
  }

  /** hls.js n'allonge le MediaSource au-delà de la playlist que si des segments
   *  y ont été posés. Passé la marge, ils l'ont été au mauvais endroit de la
   *  timeline : un épisode de 24 minutes s'est affiché en 48, et tout seek dans
   *  la seconde moitié chargeait sans fin. Le tampon n'est pas réparable en
   *  place — hls.js ne raccourcit jamais la durée — : on recharge le flux à la
   *  position courante, ramenée dans les bornes de l'épisode. */
  private checkDuration(): void {
    const hls = this.hls;
    const stream = this.stream;
    if (!hls || !stream) return;

    const index = hls.currentLevel >= 0 ? hls.currentLevel : hls.loadLevel;
    const details = hls.levels[index]?.details;
    if (!details || details.live) return;

    const expected = details.edge;
    const { duration } = this.video;

    if (!Number.isFinite(duration) || duration <= expected + DURATION_SLACK) return;

    console.warn(
      `aniweb : durée du média ${duration.toFixed(1)} s pour une playlist de ` +
        `${expected.toFixed(1)} s, rechargement du flux`,
    );

    // la même incohérence qui revient malgré les rechargements : on s'arrête
    // plutôt que de boucler
    if (this.reloads >= MAX_RELOADS) {
      this.fail("Lecture interrompue : la durée du flux est incohérente.");
      return;
    }

    this.reloads++;

    const position = Math.min(this.video.currentTime, Math.max(expected - 1, 0));

    this.attach(stream, position, !this.video.paused);
  }

  /** Secondes de vidéo déjà en tampon devant la tête de lecture. */
  private bufferAhead(): number {
    const { buffered, currentTime } = this.video;

    for (let i = 0; i < buffered.length; i++) {
      if (currentTime >= buffered.start(i) && currentTime <= buffered.end(i)) {
        return buffered.end(i) - currentTime;
      }
    }

    return 0;
  }

  /** Relevé pour le mode développeur. Reste utilisable sur un flux progressif,
   *  où hls.js n'entre pas en jeu : seuls les champs qui ont un sens sont
   *  renseignés. */
  stats(): Stats {
    const quality = this.video.getVideoPlaybackQuality?.();
    const hls = this.hls;

    const base: Stats = {
      levels: 0,
      height: this.video.videoHeight,
      bitrate: 0,
      bandwidth: 0,
      auto: false,
      buffer: this.bufferAhead(),
      frags: this.frags,
      errors: this.errors,
      latency: this.latency,
      dropped: quality?.droppedVideoFrames ?? 0,
      frames: quality?.totalVideoFrames ?? 0,
    };

    if (!hls) return base;

    // `currentLevel` vaut -1 tant qu'aucun fragment n'a été chargé ; c'est
    // `loadLevel` qui dit alors ce qui est effectivement tiré.
    const index = hls.currentLevel >= 0 ? hls.currentLevel : hls.loadLevel;
    const level = hls.levels[index];

    return {
      ...base,
      levels: hls.levels.length,
      height: level?.height || base.height,
      bitrate: level?.bitrate ?? 0,
      bandwidth: Math.round(hls.bandwidthEstimate || 0),
      auto: hls.autoLevelEnabled,
    };
  }

  /** Applique un choix de qualité, et le retient pour les épisodes suivants. */
  setQuality(quality: Quality): void {
    this.quality = quality;

    this.applyQuality();
    this.publishChoices();
  }

  /** Change le mode de préchargement. Le plafond du tampon étant fixé à la
   *  construction de hls.js, il ne prendra effet qu'à l'épisode suivant ; le
   *  préchargement des segments, lui, démarre ou s'arrête tout de suite. */
  setPreload(preload: Preload): void {
    this.preload = preload;

    if (preload !== "full") this.stopWarming();
  }

  private startWarming(urls: string[]): void {
    const abort = new AbortController();

    this.warming = abort;
    this.onPreload({ done: 0, total: urls.length });

    void warm(
      urls,
      (progress) => {
        if (!abort.signal.aborted) this.onPreload(progress);
      },
      abort.signal,
    ).finally(() => {
      if (this.warming === abort) this.warming = null;
    });
  }

  private stopWarming(): void {
    this.warming?.abort();
    this.warming = null;

    this.onPreload(null);
  }

  /** Traduit la préférence en index hls.js, -1 pour laisser faire l'ABR. */
  private resolveLevel(): number {
    const hls = this.hls;
    if (!hls || hls.levels.length === 0) return -1;

    // hls.js range ses niveaux du plus bas au plus haut
    const highest = hls.levels.length - 1;

    if (this.quality === "auto") return -1;
    if (this.quality === "best") return highest;

    // hauteur demandée : la variante la plus proche de ce que l'épisode offre,
    // car toutes n'ont pas les mêmes paliers
    const wanted = this.quality;
    let chosen = highest;
    let gap = Infinity;

    hls.levels.forEach((level, index) => {
      const distance = Math.abs((level.height || 0) - wanted);

      if (distance < gap) {
        gap = distance;
        chosen = index;
      }
    });

    return chosen;
  }

  private applyQuality(): void {
    const hls = this.hls;
    if (!hls || hls.levels.length === 0) return;

    const level = this.resolveLevel();

    if (level === -1) {
      // Auto démarre quand même sur la plus haute variante : c'est ce que l'on
      // attend en lançant un épisode, et l'ABR redescendra de lui-même si le
      // débit ne suit pas. `startLevel` ne vaut que pour le premier fragment.
      hls.startLevel = hls.levels.length - 1;
      hls.currentLevel = -1;

      return;
    }

    hls.startLevel = level;
    hls.currentLevel = level;
  }

  private publishChoices(): void {
    const hls = this.hls;

    if (!hls || hls.levels.length === 0) {
      this.onChoices([], serializeQuality(this.quality));
      return;
    }

    const choices: Choice[] = [
      { label: "Auto", value: "auto" },
      { label: "Meilleure", value: "best" },
    ];

    // présentées de la plus haute à la plus basse, l'inverse de l'ordre hls.js
    for (const level of [...hls.levels].reverse()) {
      if (!level.height) continue;

      const value = String(level.height);
      if (choices.some((choice) => choice.value === value)) continue;

      choices.push({ label: `${level.height}p`, value });
    }

    // La préférence retenue peut ne pas exister ici — 1080p demandé sur un
    // épisode qui plafonne à 720p. On affiche alors la variante réellement
    // servie, sans toucher au choix mémorisé.
    const current = serializeQuality(this.quality);

    if (choices.some((choice) => choice.value === current)) {
      this.onChoices(choices, current);
      return;
    }

    const height = hls.levels[this.resolveLevel()]?.height;

    // une variante sans hauteur déclarée n'a pas d'entrée à elle : on retombe
    // sur « Meilleure », qui décrit bien ce que `resolveLevel` a retenu
    this.onChoices(choices, height ? String(height) : "best");
  }

  destroy(): void {
    this.stopWarming();

    this.hls?.destroy();
    this.hls = null;
    this.stream = null;

    this.video.removeAttribute("src");
    this.video.load();
  }
}
